'use client';

import React from 'react';

import { Button } from '@/components/ui/button';
import useUserActions from '@/hooks/queries/useUserActions';
import useTranslation from '@/hooks/useTranslation';
import DownloadIconWhite from '@/svgs/download.svg';
import DownloadIcon from '@/svgs/download-black.svg';
import { downloadFiles } from '@/utils/downloadFiles';

type Props = {
  image: string;
  content_id: string;
  images: string[];
};

const ContentImageActions: React.FC<Props> = ({
  image,
  content_id,
  images,
}) => {
  const { t } = useTranslation('pages.content');
  const [isDownloadingImage, setIsDownloadingImage] = React.useState(false);
  const [isDownloadingAllImages, setIsDownloadingAllImages] =
    React.useState(false);
  const { mutateAsync: logUserAction, isPending } = useUserActions({
    model_id: content_id,
    model_type: 'content_bank',
  });

  const downloadAll = async () => {
    setIsDownloadingAllImages(true);
    try {
      await logUserAction({
        action: 'download_image_action',
      });
      await downloadFiles(images.map((img) => ({ url: img })));
    } finally {
      setIsDownloadingAllImages(false);
    }
  };

  const downloadImage = async () => {
    setIsDownloadingImage(true);
    try {
      await logUserAction({
        action: 'download_image_action',
      });
      await downloadFiles([{ url: image }]);
    } finally {
      setIsDownloadingImage(false);
    }
  };

  return (
    <div className="flex w-[calc(100%-16px)] justify-between items-center absolute bottom-0 right-0 p-1.5 ">
      <Button
        onClick={() => void downloadImage()}
        variant="ghost"
        style={{ backgroundColor: 'white' }}
        size="sm"
        className="p-1.5"
        disabled={isPending}
      >
        {isDownloadingImage ? (
          <div className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-gray-300 border-t-[#252525]"></div>
        ) : (
          <DownloadIcon className="size-[18px]" />
        )}
      </Button>
      {images.length > 1 && (
        <Button
          disabled={isPending}
          onClick={() => void downloadAll()}
          variant="secondary"
          className="text-[12px] font-normal p-1.5"
          size="sm"
        >
          <div className="flex items-center justify-center gap-2">
            {t('downloadAllImages')}
            {isDownloadingAllImages ? (
              <div className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-gray-300 border-t-[#252525]"></div>
            ) : (
              <DownloadIconWhite className="size-[18px]" />
            )}
          </div>
        </Button>
      )}
    </div>
  );
};

export default ContentImageActions;
